import { fetchCentre } from 'api'
import { uploadPresets } from 'app/config'
import constants, {
  ANNOUNCEMENT_TYPE_KEYS,
  ANNOUNCEMENT_TYPE_SELECT_OPTIONS,
  ANNOUNCEMENT_TYPES
} from 'app/constants'

// Validation functions
export const validateRequired = (value, fieldName) => {
  if (
    !value ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0)
  ) {
    return `${fieldName} is required`
  }
  return null
}

// Character limit validation
export const validateMaxLength = (value, maxLength, fieldName) => {
  if (value && typeof value === 'string' && value.length > maxLength) {
    return `${fieldName} must be ${maxLength} characters or less`
  }
  return null
}

const validateDateRange = (startDate, endDate) => {
  if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
    return 'End date must be after start date'
  }
  return null
}

const validateTimeRange = (startTime, endTime, startDate, endDate) => {
  if (startTime && endTime && startDate === endDate) {
    if (startTime >= endTime) {
      return 'End time must be after start time when dates are the same'
    }
  }
  return null
}

const validateDateAfterToday = (dateValue, fieldName) => {
  if (dateValue) {
    const today = new Date()
    today.setHours(0, 0, 0, 0) // Set to start of today
    const selectedDate = new Date(dateValue)

    if (selectedDate <= today) {
      return `${fieldName} must be after today`
    }
  }
  return null
}

export const AnnouncementFormFields = (isEditing = false) => [
  {
    name: 'type',
    label: 'Announcement Type',
    inputType: 'select',
    required: true,
    disabled: isEditing, // Disable when editing
    valueLabelMappings: ANNOUNCEMENT_TYPE_SELECT_OPTIONS,
    validator: (value) => validateRequired(value, 'Announcement Type')
  },
  // UI Component: Section Heading
  {
    name: 'event_details_heading',
    inputType: 'sectionHeading',
    label: 'Event Details',
    conditionalRender: (formData) => ANNOUNCEMENT_TYPES.includes(formData?.type)
  },
  {
    name: 'subtype',
    label: 'Announcement Subtype',
    inputType: 'text',
    maxLength: 12,
    required: true,
    validator: (value) => {
      const requiredError = validateRequired(value, 'Announcement Subtype')
      if (requiredError) return requiredError
      return validateMaxLength(value, 12, 'Announcement Subtype')
    },
    conditionalRender: (formData) => ANNOUNCEMENT_TYPES.includes(formData?.type)
  },
  {
    name: 'subject',
    label: 'Subject',
    inputType: 'autocomplete',
    required: true,
    validator: (value) => validateRequired(value, 'Subject'),
    conditionalRender: (formData) =>
      formData?.type === ANNOUNCEMENT_TYPE_KEYS.TRIAL ||
      formData?.type === ANNOUNCEMENT_TYPE_KEYS.PLAYDATE
  },
  {
    name: 'batch_type',
    label: 'Batch Type',
    inputType: 'text',
    required: false,
    disabled: true,
    conditionalRender: (formData) => false, // Hide the field from UI
    defaultValue: (formData) => {
      if (formData?.type === 'Trial') return 'explore'
      if (formData?.type === 'Playdate') return 'event'
      return ''
    }
  },
  {
    name: 'title',
    label: 'Announcement Title',
    inputType: 'text',
    required: true,
    maxLength: 60,
    conditionalRender: (formData) =>
      ANNOUNCEMENT_TYPES.includes(formData?.type),
    validator: (value) => {
      const requiredError = validateRequired(value, 'Announcement Title')
      if (requiredError) return requiredError
      return validateMaxLength(value, 60, 'Announcement Title')
    }
  },
  {
    name: 'description',
    label: 'Announcement Description',
    inputType: 'text',
    required: true,
    maxLength: 250,
    conditionalRender: (formData) =>
      ANNOUNCEMENT_TYPES.includes(formData?.type),
    validator: (value) => {
      const requiredError = validateRequired(value, 'Announcement Description')
      if (requiredError) return requiredError
      return validateMaxLength(value, 250, 'Announcement Description')
    }
  },
  {
    name: 'centres',
    label: 'Centres',
    inputType: 'autocomplete',
    required: true,
    multiple: true,
    conditionalRender: (formData) =>
      ANNOUNCEMENT_TYPES.includes(formData?.type),
    validator: (value) => validateRequired(value, 'Centres enabled for'),
    fetchValues: async (searchedText) => {
      try {
        const queries = {
          limit: 1000
        }
        if (searchedText) {
          queries.name = searchedText
        }
        const response = await fetchCentre({ queries })
        return response.results.map((centre) => ({
          value: centre.uuid,
          name: centre.name,
          ...centre
        }))
      } catch (error) {
        console.log('Error in fetching centres :- ', error)
        throw error
      }
    }
  },
  {
    name: 'event_start_date',
    label: 'Event Start Date',
    inputType: 'date',
    required: false,
    halfWidth: true,
    conditionalRender: (formData) =>
      formData?.type === ANNOUNCEMENT_TYPE_KEYS.MISCELLANEOUS,
    validator: (value, formData) => {
      if (value && formData?.event_end_date) {
        return validateDateRange(value, formData.event_end_date)
      }
      return null
    }
  },
  {
    name: 'event_end_date',
    label: 'Event End Date',
    inputType: 'date',
    required: false,
    halfWidth: true,
    conditionalRender: (formData) =>
      formData?.type === ANNOUNCEMENT_TYPE_KEYS.MISCELLANEOUS,
    validator: (value, formData) => {
      if (value && formData?.event_start_date) {
        return validateDateRange(formData.event_start_date, value)
      }
      return null
    }
  },
  {
    name: 'event_start_time',
    label: 'Event Start Time',
    inputType: 'time',
    required: false,
    halfWidth: true,
    conditionalRender: (formData) =>
      formData?.type === ANNOUNCEMENT_TYPE_KEYS.MISCELLANEOUS,
    validator: (value, formData) => {
      if (
        value &&
        formData?.event_end_time &&
        formData?.event_start_date === formData?.event_end_date
      ) {
        return validateTimeRange(
          value,
          formData.event_end_time,
          formData.event_start_date,
          formData.event_end_date
        )
      }
      return null
    }
  },
  {
    name: 'event_end_time',
    label: 'Event End Time',
    inputType: 'time',
    required: false,
    halfWidth: true,
    conditionalRender: (formData) =>
      formData?.type === ANNOUNCEMENT_TYPE_KEYS.MISCELLANEOUS,
    validator: (value, formData) => {
      if (
        value &&
        formData?.event_start_time &&
        formData?.event_start_date === formData?.event_end_date
      ) {
        return validateTimeRange(
          formData.event_start_time,
          value,
          formData.event_start_date,
          formData.event_end_date
        )
      }
      return null
    }
  },
  {
    name: 'tag_1',
    label: 'Tag 1',
    inputType: 'text',
    required: false,
    maxLength: 12,
    conditionalRender: (formData) =>
      formData?.type === ANNOUNCEMENT_TYPE_KEYS.MISCELLANEOUS,
    validator: (value) => validateMaxLength(value, 12, 'Tag 1')
  },
  {
    name: 'tag_2',
    label: 'Tag 2',
    inputType: 'text',
    required: false,
    maxLength: 12,
    conditionalRender: (formData) =>
      formData?.type === ANNOUNCEMENT_TYPE_KEYS.MISCELLANEOUS,
    validator: (value) => validateMaxLength(value, 12, 'Tag 2')
  },
  // UI Component: Notes Section
  {
    name: 'miscellaneous_notes',
    inputType: 'notes',
    notes: [
      'For Miscellaneous, tags 1-2 will be sorted from left to right order.',
      'Descriptions should be 120–200 characters. Longer text will be trimmed with en ellipsis (…).'
    ],
    conditionalRender: (formData) =>
      formData?.type === ANNOUNCEMENT_TYPE_KEYS.MISCELLANEOUS
  },
  {
    name: 'miscellaneous_notes',
    inputType: 'notes',
    notes: [
      'For Playdates and Trials, tags will display the duration, age range and grade range based on the respective active batches.',
      "If a centre doesn't have any Playdate or Trial active batches, the announcement will automatically stop showing in that centre.",
      'Descriptions should be 120–200 characters. Longer text will be trimmed with en ellipsis (…).'
    ],
    conditionalRender: (formData) =>
      formData?.type === ANNOUNCEMENT_TYPE_KEYS.PLAYDATE ||
      formData?.type === ANNOUNCEMENT_TYPE_KEYS.TRIAL
  },
  {
    name: 'is_cta_present',
    label: 'Add a CTA button',
    inputType: 'checkbox',
    required: false,
    conditionalRender: (formData) => ANNOUNCEMENT_TYPES.includes(formData?.type)
  },
  {
    name: 'cta',
    label: 'CTA Copy',
    inputType: 'text',
    required: true,
    maxLength: 12,
    halfWidth: true,
    conditionalRender: (formData) => formData?.is_cta_present,
    validator: (value, formData) => {
      if (formData?.is_cta_present && !value) {
        return 'CTA Copy is required'
      }
      return validateMaxLength(value, 12, 'CTA Copy')
    }
  },
  {
    name: 'cta_link',
    label: 'CTA Destination',
    inputType: 'text',
    halfWidth: true,
    required: true,
    conditionalRender: (formData) => formData?.is_cta_present,
    validator: (value, formData) => {
      if (formData?.is_cta_present && !value) {
        return 'CTA Destination is required'
      }
      return null
    }
  },
  // UI Component: External Link
  {
    name: 'deeplinks_reference',
    inputType: 'externalLink',
    linkText: 'Deeplinks Reference',
    linkUrl:
      'https://docs.google.com/spreadsheets/d/11DGJY8UnYc-Lcfv_AT4YXGzInYd2OBqmMtt2L_TJs1A/edit?usp=sharing',
    conditionalRender: (formData) => formData?.is_cta_present
  },
  {
    name: 'image',
    label: 'Banner Image *',
    inputType: 'imageUploader',
    required: true,
    conditionalRender: (formData) =>
      ANNOUNCEMENT_TYPES.includes(formData?.type),
    uploadPreset: uploadPresets[constants.ANNOUNCEMENT],
    fileUrlOnly: true,
    validator: (value) => validateRequired(value, 'Banner Image')
  },
  // UI Component: Divider
  {
    name: 'divider_1',
    inputType: 'divider',
    conditionalRender: (formData) => ANNOUNCEMENT_TYPES.includes(formData?.type)
  },
  // UI Component: Section Heading
  {
    name: 'announcement_details_heading',
    inputType: 'sectionHeading',
    label: 'Announcement Details',
    conditionalRender: (formData) => ANNOUNCEMENT_TYPES.includes(formData?.type)
  },
  {
    name: 'start_date',
    label: 'Visible From',
    inputType: 'date',
    required: true,
    halfWidth: true,
    conditionalRender: (formData) =>
      ANNOUNCEMENT_TYPES.includes(formData?.type),
    validator: (value, formData, isEditing) => {
      const requiredError = validateRequired(value, 'Visible From')
      if (requiredError) return requiredError

      // Only check "after today" validation when creating (not editing)
      if (!isEditing) {
        return validateDateAfterToday(value, 'Visible From')
      }
      return null
    },
    inputProps: {
      min: new Date().toISOString().split('T')[0] // Today as minimum date
    }
  },
  {
    name: 'end_date',
    label: 'Visible Till',
    inputType: 'date',
    required: true,
    halfWidth: true,
    conditionalRender: (formData) =>
      ANNOUNCEMENT_TYPES.includes(formData?.type),
    validator: (value, formData) => {
      const requiredError = validateRequired(value, 'Visible Till')
      if (requiredError) return requiredError

      const dateAfterTodayError = validateDateAfterToday(value, 'Visible Till')
      if (dateAfterTodayError) return dateAfterTodayError

      if (value && formData?.start_date) {
        return validateDateRange(formData.start_date, value)
      }
      return null
    },
    inputProps: {
      min: new Date().toISOString().split('T')[0] // Today as minimum date
    }
  }
]
